"use client";

import { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const stats = [
  {
    id: 'stat_connections',
    value: '10M+',
    label: 'Connections Made',
    icon: 'UsersIcon' as const
  },
  {
    id: 'stat_success',
    value: '98%',
    label: 'Match Success',
    icon: 'HeartIcon' as const
  }];


  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1696601388452-8e3b34234353"
          alt="Romantic couple holding hands at sunset with warm golden lighting"
          className="w-full h-full object-cover opacity-50"
          priority />
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/50 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-secondary/10 to-primary/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          {/* Left: Text Content */}
          <div className="lg:col-span-8 space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-xs uppercase tracking-widest font-medium text-foreground">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              AI-Powered Matchmaking
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-foreground leading-[0.95]">
              Find Your Person. <br />
              <span className="gradient-text-primary">Not Just a Match.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl font-light leading-relaxed border-l-2 border-primary/50 pl-6">
              AI-powered matchmaking that understands what you&apos;re really looking for. 
              No endless swiping. Just meaningful connections.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Link
                href="#waitlist"
                className="px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-primary/50 transition-all flex items-center gap-3 group">
                
                Join Waitlist
                <Icon
                  name="ArrowRightIcon"
                  size={20}
                  className="group-hover:translate-x-1 transition-transform" />
                
              </Link>
              <Link
                href="#how-it-works"
                className="px-8 py-4 border-2 border-white/20 text-foreground hover:bg-white/10 backdrop-blur-sm font-semibold rounded-full transition-all">
                
                See How It Works
              </Link>
            </div>
          </div>

          {/* Right: Stats Cards */}
          <div className="lg:col-span-4 flex flex-col gap-6 lg:items-end">
            {stats.map((stat, index) =>
            <div
              key={stat.id}
              className="glass-card-hover p-6 rounded-2xl w-full max-w-xs cursor-default"
              style={{
                animationDelay: isHydrated ? `${index * 0.2}s` : '0s',
                opacity: isHydrated ? 1 : 0
              }}>
              
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-muted-foreground uppercase tracking-widest font-semibold">
                    {stat.label}
                  </span>
                  <Icon
                  name={stat.icon}
                  variant="solid"
                  size={20}
                  className={index === 0 ? 'text-success' : 'text-primary'} />
                
                </div>
                <div className="text-4xl font-bold text-foreground">
                  {stat.value}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Decorative Glow Orbs */}
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] pointer-events-none" />
    </section>);

}